import { QUOTAS } from '@xb/shared';
import { type FormEvent, useId, useState } from 'react';
import { api } from '../api.js';
import { useAction, useCurrentUser, useResource } from '../hooks.js';
import { useApp } from '../pages/AppPage.js';
import { fmtDateTime } from './format.js';
import { ErrorNotice, Loading, Notice } from './Notice.js';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function parseEmails(text: string): { valid: string[]; invalid: string[] } {
  const seen = new Set<string>();
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const e = raw.trim().toLowerCase();
    if (!e || seen.has(e)) continue;
    seen.add(e);
    (EMAIL.test(e) ? valid : invalid).push(e);
  }
  return { valid, invalid };
}

export function PeopleTab() {
  const { app } = useApp();
  const me = useCurrentUser();
  const grants = useResource(() => api.listGrants(app.slug), [app.slug]);
  const [text, setText] = useState('');
  const [added, setAdded] = useState<string[] | undefined>(undefined);
  const ids = { emails: useId(), hint: useId() };

  const parsed = parseEmails(text);
  const count = grants.data?.grants.length ?? 0;
  const remaining = QUOTAS.grantsPerApp - count;

  const add = useAction(async (e: FormEvent) => {
    e.preventDefault();
    if (parsed.valid.length === 0 || parsed.invalid.length > 0) return;
    const res = await api.addGrants(app.slug, parsed.valid);
    grants.set({ grants: res.grants });
    setAdded(res.added);
    setText('');
  });

  const [revoking, setRevoking] = useState<string | undefined>(undefined);
  const revoke = useAction(async (email: string) => {
    setRevoking(email);
    try {
      await api.revokeGrant(app.slug, email);
      grants.set({ grants: (grants.data?.grants ?? []).filter((g) => g.email !== email) });
    } finally {
      setRevoking(undefined);
    }
  });

  return (
    <>
      <section className="card">
        <h2>Invite people</h2>
        <p className="small subtle">
          Add the Xero email address of each person who should be able to open this app. They'll sign in with
          Xero the first time they visit. You can always open your own app.
        </p>
        <form onSubmit={(e) => void add.run(e)} noValidate>
          <div className="field">
            <label htmlFor={ids.emails}>Email addresses</label>
            <textarea
              id={ids.emails}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setAdded(undefined);
              }}
              aria-describedby={ids.hint}
              aria-invalid={parsed.invalid.length > 0}
              placeholder={'jane@example.co.nz\nsam@example.co.nz'}
              autoComplete="off"
              spellCheck={false}
            />
            {parsed.invalid.length > 0 ? (
              <p className="error-text" id={ids.hint}>
                These don't look like email addresses: {parsed.invalid.join(', ')}
              </p>
            ) : (
              <p className="hint" id={ids.hint}>
                One per line, or separated by commas.{' '}
                {remaining > 0 ? `Room for ${remaining} more.` : 'This app is at its limit.'}
              </p>
            )}
          </div>
          <ErrorNotice error={add.error} />
          {added ? (
            <Notice kind="ok">
              {added.length === 0
                ? 'Everyone on that list already had access.'
                : `Added ${added.length}. Access changes take effect within seconds.`}
            </Notice>
          ) : null}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={add.pending || parsed.valid.length === 0 || parsed.invalid.length > 0 || remaining <= 0}
          >
            {add.pending ? 'Adding…' : parsed.valid.length > 1 ? `Add ${parsed.valid.length} people` : 'Add'}
          </button>
        </form>
      </section>

      <section className="card">
        <h2>Who has access</h2>
        <ErrorNotice error={grants.error ?? revoke.error} />
        {grants.loading && !grants.data ? <Loading /> : null}
        {grants.data ? (
          <ul className="rows">
            <li>
              <div>
                <div className="primary">{me.email}</div>
                <div className="secondary">You — the owner. Always has access.</div>
              </div>
            </li>
            {grants.data.grants.map((g) => (
              <li key={g.email}>
                <div>
                  <div className="primary">{g.email}</div>
                  <div className="secondary">
                    {g.resolvedAt
                      ? `First signed in ${fmtDateTime(g.resolvedAt)}`
                      : `Invited ${fmtDateTime(g.invitedAt)} — hasn't signed in yet`}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  disabled={revoke.pending}
                  onClick={() => void revoke.run(g.email)}
                  aria-label={`Remove access for ${g.email}`}
                >
                  {revoking === g.email ? 'Removing…' : 'Remove'}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="small subtle" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          Removing someone locks them out within seconds, even if they have the app open.
        </p>
      </section>
    </>
  );
}
